import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, Car, Lock, Globe } from "lucide-react";
import { useTableFilter, ExportBar } from "../components/TableExport";
import { Switch } from "../components/ui/switch";

export default function GestionnairesPage() {
  const [gestionnaires, setGestionnaires] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", pin: "", gerer_tous_vehicules: false });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [gRes, vRes] = await Promise.all([api.get("/users?role=gestionnaire"), api.get("/vehicles")]);
      setGestionnaires(gRes.data);
      setVehicles(vRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, role: "gestionnaire" };
      if (!payload.pin) delete payload.pin;
      if (selected) {
        await api.put(`/users/${selected.id}`, payload);
        toast.success("Gestionnaire mis a jour");
      } else {
        await api.post("/users", payload);
        toast.success("Gestionnaire ajoute");
      }
      setShowAdd(false);
      setSelected(null);
      setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "", gerer_tous_vehicules: false });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce gestionnaire ?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Gestionnaire supprime");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (g) => {
    setSelected(g);
    setForm({ nom: g.nom, prenom: g.prenom, email: g.email || "", telephone: g.telephone || "", pin: "", gerer_tous_vehicules: g.gerer_tous_vehicules || false });
    setShowAdd(true);
  };

  const getVehiclesForGestionnaire = (gId) => vehicles.filter(v => v.gestionnaire_id === gId);

  const gestExportCols = [
    { label: "Prenom", key: "prenom" },
    { label: "Nom", key: "nom" },
    { label: "Email", key: "email" },
    { label: "Telephone", key: "telephone" },
    { label: "PIN", accessor: (g) => g.has_pin ? "Configure" : "Non defini" },
    { label: "Vehicules geres", accessor: (g) => getVehiclesForGestionnaire(g.id).map(v => v.immatriculation).join(", ") || "Aucun" },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="gestionnaires-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Gestionnaires</h1>
          <p className="text-sm text-slate-500 mt-1">{gestionnaires.length} gestionnaire(s)</p>
        </div>
        <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setSelected(null); setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "" }); setShowAdd(true); }} data-testid="btn-add-gestionnaire">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <ExportBar
        searchTerm="" onSearchChange={null} searchPlaceholder={null}
        filterConfigs={[]} filters={{}} onFilterChange={() => {}} onReset={() => {}} hasActiveFilters={false}
        data={gestionnaires} columns={gestExportCols} filename="gestionnaires" title="Liste des gestionnaires"
      />

      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Nom</TableHead>
              <TableHead className="font-semibold text-slate-600">Email</TableHead>
              <TableHead className="font-semibold text-slate-600">Telephone</TableHead>
              <TableHead className="font-semibold text-slate-600">Code PIN</TableHead>
              <TableHead className="font-semibold text-slate-600">Vehicules geres</TableHead>
              <TableHead className="font-semibold text-slate-600">Acces</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gestionnaires.map(g => {
              const gVehicles = getVehiclesForGestionnaire(g.id);
              return (
                <TableRow key={g.id} data-testid={`gestionnaire-row-${g.id}`}>
                  <TableCell className="font-medium text-slate-900">{g.prenom} {g.nom}</TableCell>
                  <TableCell className="text-slate-600">{g.email || "-"}</TableCell>
                  <TableCell className="text-slate-600">{g.telephone || "-"}</TableCell>
                  <TableCell>
                    {g.has_pin ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><Lock className="w-3 h-3 mr-1" />Configure</Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">Non defini</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {gVehicles.length > 0 ? gVehicles.map(v => (
                        <Badge key={v.id} variant="outline" className="text-xs">
                          <Car className="w-3 h-3 mr-1" />{v.immatriculation}
                        </Badge>
                      )) : <span className="text-slate-400 text-sm">Aucun vehicule</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {g.gerer_tous_vehicules ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Globe className="w-3 h-3 mr-1" />Tous les vehicules</Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">Ses vehicules</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(g)} data-testid={`btn-edit-gestionnaire-${g.id}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(g.id)} data-testid={`btn-delete-gestionnaire-${g.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {gestionnaires.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                  <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  Aucun gestionnaire. Cliquez sur "Ajouter" pour en creer un.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Cards */}
      {gestionnaires.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {gestionnaires.map(g => {
            const gVehicles = getVehiclesForGestionnaire(g.id);
            const actifs = gVehicles.filter(v => v.statut === "actif").length;
            const immobilises = gVehicles.filter(v => v.statut === "immobilise").length;
            return (
              <Card key={g.id} className="hover:shadow-md transition-shadow" data-testid={`gestionnaire-card-${g.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-heading">{g.prenom} {g.nom}</CardTitle>
                      <p className="text-xs text-slate-500">{g.email || g.telephone || ""}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm">
                    <div>
                      <p className="text-lg font-bold text-slate-900 font-heading">{gVehicles.length}</p>
                      <p className="text-xs text-slate-500">Vehicule(s)</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600 font-heading">{actifs}</p>
                      <p className="text-xs text-slate-500">Actif(s)</p>
                    </div>
                    {immobilises > 0 && (
                      <div>
                        <p className="text-lg font-bold text-red-600 font-heading">{immobilises}</p>
                        <p className="text-xs text-slate-500">Immobilise(s)</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="gestionnaire-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected ? "Modifier le gestionnaire" : "Ajouter un gestionnaire"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prenom</Label>
                <Input value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} required data-testid="input-gestionnaire-prenom" />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} required data-testid="input-gestionnaire-nom" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="input-gestionnaire-email" />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} data-testid="input-gestionnaire-telephone" />
              </div>
            </div>
            <div>
              <Label>Code PIN <span className="text-xs text-slate-400 font-normal">(4 ou 6 chiffres, optionnel{selected ? " - laisser vide pour ne pas modifier" : ""})</span></Label>
              <Input
                type="password"
                placeholder={selected ? "Laisser vide pour conserver l'actuel" : "Ex: 1234 ou 123456"}
                maxLength={6}
                value={form.pin}
                onChange={e => setForm({...form, pin: e.target.value.replace(/\D/g, "")})}
                data-testid="input-gestionnaire-pin"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid="toggle-gerer-tous">
              <div>
                <Label className="text-sm font-medium">Gerer tous les vehicules</Label>
                <p className="text-xs text-slate-500">Permet au gestionnaire d'acceder et gerer tous les vehicules, meme ceux affectes a d'autres gestionnaires</p>
              </div>
              <Switch checked={form.gerer_tous_vehicules} onCheckedChange={v => setForm({...form, gerer_tous_vehicules: v})} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-gestionnaire">
                {selected ? "Modifier" : "Ajouter"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
